import { Controller } from "@hotwired/stimulus"
import * as THREE from "three"

export default class extends Controller {
  connect() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    this.initThree()
    this.animate()
    this._onResize = this.onWindowResize.bind(this)
    this._onMouse = this.onMouseMove.bind(this)
    window.addEventListener('resize', this._onResize)
    window.addEventListener('mousemove', this._onMouse)
  }

  disconnect() {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    if (this._onResize) window.removeEventListener('resize', this._onResize)
    if (this._onMouse) window.removeEventListener('mousemove', this._onMouse)
    if (this.renderer) {
      this.renderer.dispose()
      this.element.innerHTML = ''
    }
  }

  initThree() {
    const w = this.element.clientWidth || window.innerWidth
    const h = this.element.clientHeight || window.innerHeight

    this.scene = new THREE.Scene()
    this.camera = new THREE.PerspectiveCamera(60, w / h, 0.1, 1000)
    this.camera.position.z = 28

    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true })
    this.renderer.setSize(w, h)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.domElement.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;'
    this.element.appendChild(this.renderer.domElement)

    // Particle sphere
    const count = 2500
    const positions = new Float32Array(count * 3)
    const colors = new Float32Array(count * 3)

    const palette = [
      new THREE.Color(0xf97316), // orange-500
      new THREE.Color(0xfb923c), // orange-400
      new THREE.Color(0xf59e0b), // amber-500
      new THREE.Color(0xfbbf24), // amber-400
      new THREE.Color(0xfdba74), // orange-300
    ]

    for (let i = 0; i < count; i++) {
      // Uniform distribution on sphere surface
      const u = Math.random()
      const v = Math.random()
      const theta = 2 * Math.PI * u
      const phi = Math.acos(2 * v - 1)
      const r = 12 + Math.random() * 3

      positions[i * 3]     = r * Math.sin(phi) * Math.cos(theta)
      positions[i * 3 + 1] = r * Math.cos(phi)
      positions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta)

      const c = palette[Math.floor(Math.random() * palette.length)]
      colors[i * 3]     = c.r
      colors[i * 3 + 1] = c.g
      colors[i * 3 + 2] = c.b
    }

    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))

    const material = new THREE.PointsMaterial({
      size: 0.13,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      sizeAttenuation: true,
    })

    this.sphere = new THREE.Points(geometry, material)
    this.scene.add(this.sphere)

    this.mouseX = 0
    this.mouseY = 0
  }

  onMouseMove(e) {
    this.mouseX = (e.clientX / window.innerWidth - 0.5) * 2
    this.mouseY = (e.clientY / window.innerHeight - 0.5) * 2
  }

  onWindowResize() {
    const w = this.element.clientWidth || window.innerWidth
    const h = this.element.clientHeight || window.innerHeight
    this.camera.aspect = w / h
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(w, h)
  }

  animate() {
    this.rafId = requestAnimationFrame(this.animate.bind(this))
    const t = Date.now() * 0.0003

    if (this.sphere) {
      this.sphere.rotation.y = t * 0.4
      this.sphere.rotation.x = t * 0.08
    }

    // Gentle mouse parallax
    this.camera.position.x += (this.mouseX * 2 - this.camera.position.x) * 0.03
    this.camera.position.y += (-this.mouseY * 2 - this.camera.position.y) * 0.03
    this.camera.lookAt(this.scene.position)

    this.renderer.render(this.scene, this.camera)
  }
}
